import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import type { FinCustomReport, FinancialReport } from '@/types/finance';

const BASE_REPORTS = [
  { value: '', label: 'Account balances (by type)' },
  { value: 'PROFIT_LOSS', label: 'Profit & Loss (filtered)' },
  { value: 'BALANCE_SHEET', label: 'Balance Sheet (filtered)' },
  { value: 'CASH_FLOW', label: 'Cash Flow (filtered)' },
  { value: 'AR_AGING', label: 'AR Aging (filtered)' },
];

export function FinanceReportBuilderPage() {
  const [reports, setReports] = useState<FinCustomReport[]>([]);
  const [reportCode, setReportCode] = useState('');
  const [reportName, setReportName] = useState('');
  const [baseReportType, setBaseReportType] = useState('');
  const [filterLabels, setFilterLabels] = useState('');
  const [accountTypes, setAccountTypes] = useState('ASSET,LIABILITY,EQUITY');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [preview, setPreview] = useState<FinancialReport | null>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const previewLines = preview?.lines ?? [];
  const { rows: paginatedPreviewLines, tablePaginationProps: previewPaginationProps } =
    useClientPaginatedRows(previewLines);

  const load = () => {
    financeApi.listCustomReports().then(setReports).catch((e) => showApiError(e, 'Failed to load reports'));
  };

  useEffect(() => {
    load();
  }, []);

  const saveReport = async (e: FormEvent) => {
    e.preventDefault();
    if (!reportCode.trim() || !reportName.trim()) return;
    setSaving(true);
    try {
      const configJson: Record<string, unknown> = baseReportType
        ? { includeLabels: filterLabels.split(',').map((s) => s.trim()).filter(Boolean) }
        : { accountTypes: accountTypes.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) };
      await financeApi.saveCustomReport({
        reportCode: reportCode.trim(),
        reportName: reportName.trim(),
        baseReportType: baseReportType || undefined,
        configJson,
      });
      notify.success('Report saved');
      setReportCode('');
      setReportName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const runReport = async (id: number) => {
    setSelectedId(id);
    setRunning(true);
    try {
      setPreview(await financeApi.runCustomReport(id));
    } catch (err) {
      showApiError(err, 'Failed to run report');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Report builder"
        description="Save and run custom financial report definitions"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Reports', to: '/finance/reports' },
          { label: 'Builder' },
        ]}
        actions={
          <ButtonLink to="/finance/reports" variant="secondary" size="sm">
            Standard reports
          </ButtonLink>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4 space-y-4">
          <h3 className="font-semibold">Saved reports</h3>
          <ul className="text-sm space-y-2">
            {reports.map((r) => (
              <li key={r.id} className="flex items-center justify-between border rounded p-2">
                <div>
                  <div className="font-medium">{r.reportName}</div>
                  <div className="text-xs text-slate-500">{r.reportCode} · {r.baseReportType ?? 'ACCOUNT'}</div>
                </div>
                <Button
                  variant="secondary"
                  loading={running && selectedId === r.id}
                  onClick={() => void runReport(r.id)}
                >
                  Run
                </Button>
              </li>
            ))}
            {reports.length === 0 && <li className="text-slate-500">No custom reports yet.</li>}
          </ul>

          <form onSubmit={saveReport} className="space-y-3 border-t pt-4">
            <h4 className="font-medium">New report</h4>
            <Input label="Report code" value={reportCode} onChange={(e) => setReportCode(e.target.value)} />
            <Input label="Report name" value={reportName} onChange={(e) => setReportName(e.target.value)} />
            <Select
              label="Base report"
              options={BASE_REPORTS}
              value={baseReportType}
              onChange={setBaseReportType}
            />
            {baseReportType ? (
              <Input
                label="Include labels (comma-separated keywords)"
                value={filterLabels}
                onChange={(e) => setFilterLabels(e.target.value)}
                placeholder="revenue, expense, cash"
              />
            ) : (
              <Input
                label="Account types"
                value={accountTypes}
                onChange={(e) => setAccountTypes(e.target.value)}
              />
            )}
            <Button type="submit" loading={saving}>Save report</Button>
          </form>
        </div>

        <div className="card p-4">
          <h3 className="font-semibold mb-3">Preview</h3>
          {!preview ? (
            <p className="text-sm text-slate-500">Run a saved report to preview results.</p>
          ) : (
            <>
            <table className="w-full text-sm">
              <tbody>
                {paginatedPreviewLines.map((line, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-2">{line.label}</td>
                    <td className="py-2 text-right font-mono">{line.amount?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t font-semibold">
                  <td className="py-2">Total</td>
                  <td className="py-2 text-right font-mono">{preview.total?.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
            <TablePagination {...previewPaginationProps} hasRows={previewLines.length > 0} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
